export interface Profile {
   label: string
   defaultSqFt: number
   ticket: number
   space: number
   equipment: number
   foh: number
   licensing: number
   inventory: number
   marketing: number
   working: number
}

export const PROFILES: Record<string, Profile> = {
   "food-truck": {
      label: "Food Truck",
      defaultSqFt: 200,
      ticket: 15,
      space: 48000,
      equipment: 28000,
      foh: 1500,
      licensing: 2800,
      inventory: 4500,
      marketing: 7000,
      working: 32500,
   },
   "coffee-shop": {
      label: "Coffee Shop",
      defaultSqFt: 1400,
      ticket: 10,
      space: 78000,
      equipment: 52000,
      foh: 18000,
      licensing: 6500,
      inventory: 9000,
      marketing: 11000,
      working: 48500,
   },
   "fast-casual": {
      label: "Fast Casual",
      defaultSqFt: 2500,
      ticket: 18,
      space: 145000,
      equipment: 88000,
      foh: 32000,
      licensing: 14000,
      inventory: 16000,
      marketing: 18000,
      working: 68500,
   },
   "bar-brewery": {
      label: "Bar / Brewery",
      defaultSqFt: 3000,
      ticket: 25,
      space: 195000,
      equipment: 115000,
      foh: 48000,
      licensing: 44000,
      inventory: 24000,
      marketing: 22000,
      working: 90000,
   },
   "full-service": {
      label: "Full Service",
      defaultSqFt: 4000,
      ticket: 35,
      space: 236000,
      equipment: 144000,
      foh: 62000,
      licensing: 38000,
      inventory: 32000,
      marketing: 28000,
      working: 118000,
   },
   "fine-dining": {
      label: "Fine Dining",
      defaultSqFt: 5500,
      ticket: 75,
      space: 460000,
      equipment: 210000,
      foh: 115000,
      licensing: 78000,
      inventory: 55000,
      marketing: 52000,
      working: 194500,
   },
}

export interface CategoryItem {
   id: string
   name: string
   desc: string
}

export interface Category {
   label: string
   color: string
   items: CategoryItem[]
}

export const CATEGORIES: Record<string, Category> = {
   space: {
      label: "Vehicle & Mobile / Space Asset Base",
      color: "#163785",
      items: [
         {
            id: "vehicle",
            name: "Vehicle / Property Physical Structure",
            desc: "Core infrastructure setup, operational facility framing, or truck base.",
         },
         {
            id: "wrap",
            name: "Vehicle Wrap & Brand Facade Work",
            desc: "High performance exterior visual graphic installations.",
         },
         {
            id: "generator",
            name: "Generator & Primary Power Utilities",
            desc: "Commercial line load power units and distribution utilities.",
         },
      ],
   },
   equipment: {
      label: "Kitchen Equipment Infrastructure",
      color: "#f15a29",
      items: [
         {
            id: "cooking",
            name: "Cooking Line Suite (Range, oven, fryer)",
            desc: "Heavy-duty processing configurations, cooking elements.",
         },
         {
            id: "refrigeration",
            name: "Refrigeration Arrays (Walk-in, reach-in)",
            desc: "High variance cooling networks.",
         },
         {
            id: "smallwares",
            name: "Smallwares Setup (Pots, pans, tools)",
            desc: "Full culinary staging small utility packages.",
         },
         { id: "dishwashing", name: "Dishwashing Staging System Hardware", desc: "Code-compliant cleaning arrays." },
         {
            id: "ventilation",
            name: "Ventilation & Custom Air Hood Systems",
            desc: "Suppressive fire extraction networks.",
         },
      ],
   },
   foh: {
      label: "Front of House Touchpoints",
      color: "#009DDB",
      items: [
         {
            id: "furniture",
            name: "Tables & Chairs (Dining furniture arrays)",
            desc: "Customer orientation layout elements.",
         },
         {
            id: "pos",
            name: "Omnichannel POS System (Terminals, printers)",
            desc: "Centralized transaction registries.",
         },
         { id: "bar_equip", name: "Bar Equipment Configuration", desc: "Wet area speed wells, collection line grids." },
         {
            id: "decor",
            name: "Décor & Interior Finishing Aesthetics",
            desc: "Ambiance properties and interior styling accents.",
         },
      ],
   },
   licensing: {
      label: "Licenses & Legal Controls",
      color: "#FFB02E",
      items: [
         {
            id: "biz_license",
            name: "Business License & Commercial Registrations",
            desc: "Corporate filing logs, transactional clearings.",
         },
         {
            id: "food_license",
            name: "Food License & Primary Health Clearings",
            desc: "Zoning clearings and sanitation agency logs.",
         },
         {
            id: "liquor_license",
            name: "Liquor Licensure Permissions",
            desc: "Spirits or beer and wine licenses (Conditional on status).",
         },
         {
            id: "safety_permits",
            name: "Health & Fire Occupancy Permits",
            desc: "Safety clearings and code verification audits.",
         },
         {
            id: "legal_cpa",
            name: "Professional Legal & CPA Representation",
            desc: "Entity creation tasks, contract reviews.",
         },
      ],
   },
   inventory: {
      label: "Initial Opening Inventory",
      color: "#8B5CF6",
      items: [
         {
            id: "raw_f_b",
            name: "Food & Beverage Opening Stock Baseline",
            desc: "Initial component volume stock reserves.",
         },
         {
            id: "alcohol_stock",
            name: "Alcoholic Product Reserve",
            desc: "Opening inventory allocations for the beverage service track.",
         },
         { id: "supplies", name: "Paper Goods & Serving Supplies", desc: "Fulfillment packaging, client containers." },
         {
            id: "cleaning_chem",
            name: "Cleaning Assets & Operational Chemicals",
            desc: "Sanitization compounds, maintenance items.",
         },
      ],
   },
   marketing: {
      label: "Marketing & Brand Launch Action Frameworks",
      color: "#EC4899",
      items: [
         {
            id: "branding",
            name: "Brand Identity & Visual Logo Design",
            desc: "Visual design templates, typography packages.",
         },
         {
            id: "website",
            name: "Corporate Website & Web Development",
            desc: "Integrated ordering system digital platforms.",
         },
         {
            id: "opening_event",
            name: "Grand Opening Activation Campaigns",
            desc: "Launch event activation metrics, community checks.",
         },
         {
            id: "advertising",
            name: "Launch Visibility Phase (First 90 Days)",
            desc: "Hyperlocal geofenced ads and media allocations.",
         },
      ],
   },
   working: {
      label: "Working Capital Operational Airbag",
      color: "#64748B",
      items: [
         { id: "rent_buffer", name: "Rent Protection Reserve (3 Months)", desc: "Operational lease tracking buffer." },
         {
            id: "payroll_reserve",
            name: "Payroll Reserve Cushion (3 Months)",
            desc: "Core labor maintenance during initial runway phases.",
         },
         {
            id: "utilities",
            name: "Utility Deposits & Grid Connection Elements",
            desc: "Resource initialization connectivity outlays.",
         },
         {
            id: "insurance_annual",
            name: "Annualized General Asset Insurance",
            desc: "General property and operational liability coverages.",
         },
         {
            id: "emergency_buffer",
            name: "Emergency Capital Variance Discretionary Buffer",
            desc: "Safety variance floor managing surprise delays.",
         },
      ],
   },
}
